import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Header } from '@aptsi-types';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { EnvironmentService } from '../../services/environment.service';
import { AssetService } from '../../services/asset.service';
import { ZoneService } from '../../services/zone.service';
import { SuccessDialogComponent } from 'src/app/shared/dialogs/success-dialog/success-dialog.component';
import { ErrorDialogComponent } from 'src/app/shared/dialogs/error-dialog/error-dialog.component';
import { WorkflowNavigationService } from '../../../shared/workflow-navigation.service';

type WizardStep = 1 | 2 | 3 | 4;
type ResourceRecord = Record<string, unknown>;
type ResourceKind = 'environments' | 'assets';

interface ResourceOption {
  id: string;
  name: string;
  description?: string;
  category?: string;
}

interface ListState {
  rows: ResourceRecord[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  searchText: string;
  filterByHeader: string;
  isSearch: boolean;
  isLoading: boolean;
  isSearching: boolean;
  loadError: boolean;
  searchError: boolean;
  loaded: boolean;
}

function createListState(): ListState {
  return {
    rows: [],
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    searchText: '',
    filterByHeader: 'all',
    isSearch: false,
    isLoading: false,
    isSearching: false,
    loadError: false,
    searchError: false,
    loaded: false
  };
}

@Component({
  selector: 'app-zone-create-or-update',
  templateUrl: './zone-create-or-update.component.html',
  styleUrls: ['./zone-create-or-update.component.scss']
})
export class ZoneCreateOrUpdateComponent implements OnInit, OnDestroy {
  readonly itemsPerPage = 7;
  readonly steps: Array<{ id: WizardStep; label: string; icon: string }> = [
    { id: 1, label: 'Zone Details', icon: 'bi-shield-lock' },
    { id: 2, label: 'Environments', icon: 'bi-diagram-3' },
    { id: 3, label: 'System Assets', icon: 'bi-hdd-network' },
    { id: 4, label: 'Review', icon: 'bi-clipboard-check' }
  ];

  readonly environmentHeaders: Header[] = [
    { name: 'Environment ID', property: 'id', showInDropdown: true, description: 'Unique identifier of the environment' },
    { name: 'Environment Name', property: 'environmentName', showInDropdown: true, description: 'Name of the environment' },
    { name: 'Description', property: 'description', showInDropdown: true, description: 'Description of the environment' }
  ];

  readonly assetHeaders: Header[] = [
    { name: 'Asset ID', property: 'id', showInDropdown: true, description: 'Unique identifier of the system asset' },
    { name: 'Asset Name', property: 'assetName', showInDropdown: true, description: 'Name of the system asset' },
    { name: 'Description', property: 'description', showInDropdown: true, description: 'Description of the system asset' },
    { name: 'Business Value', property: 'businessValue', showInDropdown: true, description: 'Business value of the asset' },
    { name: 'Data Classification', property: 'dataClassification', showInDropdown: true, description: 'Data classification of the asset' }
  ];

  readonly noActionsConfig = {
    map: false, update: false, view: false, delete: false,
    import: false, configure: false, submit: false, showAssets: false
  };

  form: FormGroup;
  currentStep: WizardStep = 1;
  isCreate = true;
  zoneId = '';
  isSubmitting = false;
  submitError = '';
  submitSuccess = false;
  pageError = '';
  isLoadingZone = false;
  isLoadingTypes = false;
  securityZoneTypes: ResourceOption[] = [];
  platforms: ResourceOption[] = [];
  isLoadingPlatforms = false;
  platformLoadError = false;

  readonly environmentsState = createListState();
  readonly assetsState = createListState();
  selectedEnvironmentIds: string[] = [];
  selectedAssetIds: string[] = [];

  private readonly selectedEnvironmentRecords = new Map<string, ResourceRecord>();
  private readonly selectedAssetRecords = new Map<string, ResourceRecord>();
  private readonly environmentSearch$ = new Subject<void>();
  private readonly assetSearch$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private existingZone: ResourceRecord | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly zoneService: ZoneService,
    private readonly environmentService: EnvironmentService,
    private readonly assetService: AssetService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
     private readonly workflowNavigation: WorkflowNavigationService
  ) {
    this.form = this.fb.group({
      zoneName: ['', [Validators.required, Validators.maxLength(120)]],
      securityZoneTypeId: ['', Validators.required],
      mappingDescription: ['', Validators.maxLength(1000)],
      description: ['', Validators.maxLength(1000)],
      risk: [''],
      platformName: ['', Validators.required],
      assetType: ['']
    });
  }

  ngOnInit(): void {
    this.zoneId = this.route.snapshot.paramMap.get('zoneId')
      ?? this.route.snapshot.paramMap.get('securityZoneId')
      ?? this.route.snapshot.paramMap.get('id')
      ?? this.route.snapshot.queryParamMap.get('zoneId')
      ?? this.route.snapshot.queryParamMap.get('securityZoneId')
      ?? this.route.snapshot.queryParamMap.get('id')
      ?? '';
    this.isCreate = this.route.snapshot.data['create'] === true || !this.zoneId;

    this.environmentSearch$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => void this.fetchEnvironments(true));
    this.assetSearch$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => void this.fetchAssets(true));

    void this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.environmentSearch$.complete();
    this.assetSearch$.complete();
  }

  get selectedEnvironments(): ResourceRecord[] {
    return this.selectedEnvironmentIds.map(id => this.selectedEnvironmentRecords.get(id)
      ?? ({ id, environmentName: 'Selected environment' } as ResourceRecord));
  }

  get selectedAssets(): ResourceRecord[] {
    return this.selectedAssetIds.map(id => this.selectedAssetRecords.get(id)
      ?? ({ id, assetName: 'Selected system asset' } as ResourceRecord));
  }

  get isLastStep(): boolean {
    return this.currentStep === 4;
  }

  getSecurityZoneTypeName(): string {
    const selectedId = this.asId(this.form.get('securityZoneTypeId')?.value);
    return this.securityZoneTypes.find(type => type.id === selectedId)?.name ?? '';
  }

  private async initialize(): Promise<void> {
    this.pageError = '';
    this.isLoadingTypes = true;
    let typesLoaded = false;
    try {
      const response = await this.zoneService.getSecurityZoneTypes().toPromise();
      this.securityZoneTypes = this.collectionFrom(response).map(item => ({
        id: this.asId(item['id'] ?? item['securityZoneTypeId']),
        name: String(item['name'] ?? item['securityZoneTypeName'] ?? ''),
        description: this.optionalString(item['description'])
      })).filter(item => item.id && item.name);
      typesLoaded = true;
    } catch (error) {
      console.error('Unable to load security zone types:', error);
      this.pageError = 'Unable to load security zone types. Please retry.';
    } finally {
      this.isLoadingTypes = false;
      this.refreshView();
    }

    if (!typesLoaded) return;

    if (!this.isCreate && this.zoneId) {
      await this.loadExistingZone();
    }
  }

  async retryInitialize(): Promise<void> {
    await this.initialize();
  }

private async loadExistingZone(): Promise<void> {
  this.pageError = '';
  this.isLoadingZone = true;

  try {
    // Fetch the existing security zone.
    const response = await this.zoneService
      .getZone(this.zoneId)
      .toPromise();

    // Preserve the complete API response because environmentIds and
    // assetIds are siblings of securityZone in the response.
    const responseRecord = this.asRecord(response);

    // Extract the actual security zone object.
    const zone = this.recordFrom(response, ['zone', 'securityZone']);

    if (!zone) {
      throw new Error(
        'The zone response did not contain a zone object.'
      );
    }

    this.existingZone = zone;

    // ---------------------------------------------------------
    // 1. Populate security zone type
    // ---------------------------------------------------------
    const typeObject = this.asRecord(
      zone['securityZoneType'] ?? zone['zoneType']
    );

    const typeId = this.asId(
      zone['securityZoneTypeId'] ??
      zone['zoneTypeId'] ??
      typeObject?.['id']
    );

    const typeName = this.asId(
      zone['securityZoneTypeName'] ??
      typeObject?.['name'] ??
      zone['securityZoneType']
    );

    const matchedType =
      this.securityZoneTypes.find(type => type.id === typeId) ??
      this.securityZoneTypes.find(
        type => type.name.toLowerCase() === typeName.toLowerCase()
      );

    // ---------------------------------------------------------
    // 2. Populate platform
    // ---------------------------------------------------------
    const platformObject = this.asRecord(zone['platform']);

    const platformName = this.asId(
      zone['platformName'] ?? platformObject?.['name']
    );

    // ---------------------------------------------------------
    // 3. Populate the form
    // ---------------------------------------------------------
    this.form.patchValue(
      {
        zoneName: zone['zoneName'] ?? zone['name'] ?? '',
        securityZoneTypeId: matchedType?.id ?? typeId,
        mappingDescription:
          zone['mappingDescription'] ??
          zone['mapping_description'] ??
          '',
        description: zone['description'] ?? '',
        risk: zone['risk'] ?? '',
        platformName,
        assetType: zone['assetType'] ?? ''
      },
      { emitEvent: false }
    );

    // ---------------------------------------------------------
    // 4. Extract environment and asset IDs
    // ---------------------------------------------------------
    // IMPORTANT:
    // The API response has this structure:
    //
    // {
    //   securityZone: { ... },
    //   environmentIds: ['environment-id'],
    //   assetIds: ['asset-id']
    // }
    //
    // Therefore, extract the association IDs from responseRecord,
    // not from the inner zone object.

    const associationSource: ResourceRecord =
      responseRecord ?? zone;

    const environmentIds = this.extractAssociationIds(
      associationSource,
      [
        'environmentIds',
        'environmentIDs',
        'environments'
      ],
      [
        'environmentId',
        'id',
        'environment_id'
      ]
    );

    const assetIds = this.extractAssociationIds(
      associationSource,
      [
        'assetIds',
        'systemAssetIds',
        'assets',
        'systemAssets'
      ],
      [
        'assetId',
        'systemAssetId',
        'id',
        'asset_id'
      ]
    );

    // Store the selected IDs. These arrays are bound to the tables
    // through [selectedRowKeys].
    this.selectedEnvironmentIds = this.uniqueIds(environmentIds);
    this.selectedAssetIds = this.uniqueIds(assetIds);

    // ---------------------------------------------------------
    // 5. Seed the selected-record caches
    // ---------------------------------------------------------
    // If the API provides only IDs, the helpers retain placeholder
    // records until the corresponding table data is loaded.

    this.seedAssociationRecords(
      associationSource['environments'] ??
        associationSource['environmentIds'],
      this.selectedEnvironmentRecords,
      this.selectedEnvironmentIds,
      'environmentName'
    );

    this.seedAssociationRecords(
      associationSource['assets'] ??
        associationSource['systemAssets'] ??
        associationSource['assetIds'],
      this.selectedAssetRecords,
      this.selectedAssetIds,
      'assetName'
    );

    // ---------------------------------------------------------
    // 6. Load platforms for the selected zone type
    // ---------------------------------------------------------
    if (matchedType?.name || typeName) {
      await this.fetchPlatformsForType(
        matchedType?.name ?? typeName,
        platformName
      );
    }

    console.log(
      'Loaded zone:',
      zone
    );

    console.log(
      'Selected environment IDs:',
      this.selectedEnvironmentIds
    );

    console.log(
      'Selected asset IDs:',
      this.selectedAssetIds
    );

  } catch (error) {
    console.error(
      'Unable to load the existing zone:',
      error
    );

    this.pageError =
      'Unable to load the existing zone. Check the zone ID and try again.';

  } finally {
    this.isLoadingZone = false;
    this.refreshView();
  }
}

  async onSecurityZoneTypeChanged(): Promise<void> {
    const selectedId = this.asId(this.form.get('securityZoneTypeId')?.value);
    const selectedType = this.securityZoneTypes.find(type => type.id === selectedId);
    this.form.patchValue({ platformName: '' }, { emitEvent: false });
    this.platforms = [];
    if (selectedType) {
      await this.fetchPlatformsForType(selectedType.name);
    }
  }

  private async fetchPlatformsForType(typeName: string, keepPlatformName = ''): Promise<void> {
    if (!typeName) {
      this.platforms = [];
      return;
    }
    this.isLoadingPlatforms = true;
    this.platformLoadError = false;
    try {
      const response = await this.zoneService.getPlatformsBySecurityZoneType(typeName).toPromise();
      const fetched = this.collectionFrom(response).map(item => ({
        id: this.asId(item['id'] ?? item['platformId']),
        name: String(item['name'] ?? item['platformName'] ?? ''),
        description: this.optionalString(item['description']),
        category: this.optionalString(item['category'])
      })).filter(item => item.id && item.name);

      if (keepPlatformName && !fetched.some(platform => platform.name === keepPlatformName)) {
        const existingPlatformId = this.asId(this.existingZone?.['platformId']);
        fetched.unshift({ id: existingPlatformId, name: keepPlatformName, description: '', category: '' });
      }
      this.platforms = fetched;
      if (keepPlatformName) {
        this.form.patchValue({ platformName: keepPlatformName }, { emitEvent: false });
      }
    } catch (error) {
      console.error('Unable to load platforms for security zone type:', error);
      this.platformLoadError = true;
    } finally {
      this.isLoadingPlatforms = false;
      this.refreshView();
    }
  }

  async retryPlatforms(): Promise<void> {
    const selectedType = this.securityZoneTypes.find(type => type.id === this.asId(this.form.get('securityZoneTypeId')?.value));
    if (selectedType) {
      await this.fetchPlatformsForType(selectedType.name, this.asId(this.form.get('platformName')?.value));
    }
  }

  async fetchEnvironments(search = false): Promise<void> {
    const state = this.environmentsState;
    if (search && !state.searchText.trim()) {
      state.isSearch = false;
      state.currentPage = 1;
      await this.fetchEnvironments(false);
      return;
    }
    if (search) {
      state.isSearching = true;
      state.searchError = false;
    } else {
      state.isLoading = true;
      state.loadError = false;
    }

    try {
      const response = search
        ? await this.environmentService.getEnvironmentsSearch(state.currentPage - 1, state.searchText.trim(), state.filterByHeader).toPromise()
        : await this.environmentService.getEnvironments(state.currentPage - 1).toPromise();
      const rawRows = this.collectionFrom(response);
      const first = rawRows[0];
      const rows = rawRows.map(item => this.normalizeEnvironment(item));
      state.rows = rows;
      state.totalItems = this.readNumber(first?.['totalEnvironments'] ?? first?.['totalRecords'] ?? first?.['totalItems'], rows.length);
      state.totalPages = this.readNumber(first?.['totalPages'], state.totalItems ? Math.ceil(state.totalItems / this.itemsPerPage) : 0);
      state.loaded = true;
      state.isSearch = search;
      this.updateSelectedRecords(rows, this.selectedEnvironmentIds, this.selectedEnvironmentRecords);
    } catch (error) {
      console.error('Unable to fetch environments:', error);
      if (search) state.searchError = true;
      else state.loadError = true;
    } finally {
      state.isLoading = false;
      state.isSearching = false;
      this.refreshView();
    }
  }

  async fetchAssets(search = false): Promise<void> {
    const state = this.assetsState;
    if (search && !state.searchText.trim()) {
      state.isSearch = false;
      state.currentPage = 1;
      await this.fetchAssets(false);
      return;
    }
    if (search) {
      state.isSearching = true;
      state.searchError = false;
    } else {
      state.isLoading = true;
      state.loadError = false;
    }

    try {
      const response = search
        ? await this.assetService.getAssetsSearch(state.currentPage - 1, state.searchText.trim(), state.filterByHeader).toPromise()
        : await this.assetService.getAssets(state.currentPage - 1).toPromise();
      const rawRows = this.collectionFrom(response);
      const first = rawRows[0];
      const rows = rawRows.map(item => this.normalizeAsset(item));
      state.rows = rows;
      state.totalItems = this.readNumber(first?.['totalAssets'] ?? first?.['totalSystemAssets'] ?? first?.['totalRecords'] ?? first?.['totalItems'], rows.length);
      state.totalPages = this.readNumber(first?.['totalPages'], state.totalItems ? Math.ceil(state.totalItems / this.itemsPerPage) : 0);
      state.loaded = true;
      state.isSearch = search;
      this.updateSelectedRecords(rows, this.selectedAssetIds, this.selectedAssetRecords);
    } catch (error) {
      console.error('Unable to fetch system assets:', error);
      if (search) state.searchError = true;
      else state.loadError = true;
    } finally {
      state.isLoading = false;
      state.isSearching = false;
      this.refreshView();
    }
  }

  onSearchInput(kind: ResourceKind): void {
    const state = kind === 'environments' ? this.environmentsState : this.assetsState;
    state.currentPage = 1;
    if (kind === 'environments') this.environmentSearch$.next();
    else this.assetSearch$.next();
  }

  onFilterChanged(kind: ResourceKind): void {
    const state = kind === 'environments' ? this.environmentsState : this.assetsState;
    state.currentPage = 1;
    if (state.searchText.trim()) {
      if (kind === 'environments') void this.fetchEnvironments(true);
      else void this.fetchAssets(true);
    } else if (kind === 'environments') {
      void this.fetchEnvironments(false);
    } else {
      void this.fetchAssets(false);
    }
  }

  submitSearch(kind: ResourceKind): void {
    const state = kind === 'environments' ? this.environmentsState : this.assetsState;
    if (kind === 'environments') void this.fetchEnvironments(!!state.searchText.trim());
    else void this.fetchAssets(!!state.searchText.trim());
  }

  clearSearch(kind: ResourceKind): void {
    const state = kind === 'environments' ? this.environmentsState : this.assetsState;
    state.searchText = '';
    state.filterByHeader = 'all';
    state.isSearch = false;
    state.currentPage = 1;
    if (kind === 'environments') void this.fetchEnvironments(false);
    else void this.fetchAssets(false);
  }

  changePage(kind: ResourceKind, page: number | { pageIndex?: number; currentPage?: number }): void {
    const state = kind === 'environments' ? this.environmentsState : this.assetsState;
    const requestedPage = typeof page === 'number'
      ? page
      : (page.currentPage ?? ((page.pageIndex ?? 0) + 1));
    state.currentPage = Math.max(1, requestedPage);
    if (kind === 'environments') void this.fetchEnvironments(state.isSearch);
    else void this.fetchAssets(state.isSearch);
  }

  onSelectedRowsChange(kind: ResourceKind, selectedRows: ResourceRecord[]): void {
    const state = kind === 'environments' ? this.environmentsState : this.assetsState;
    const currentIds = kind === 'environments' ? this.selectedEnvironmentIds : this.selectedAssetIds;
    const recordCache = kind === 'environments' ? this.selectedEnvironmentRecords : this.selectedAssetRecords;
    const visibleIds = this.uniqueIds(state.rows.map(row => this.asId(row['id'])));
    const selectedVisibleIds = this.uniqueIds((selectedRows ?? []).map(row => this.asId(row['id'])));
    const nextIds = currentIds.filter(id => !visibleIds.includes(id));
    selectedVisibleIds.forEach(id => {
      if (!nextIds.includes(id)) nextIds.push(id);
    });

    visibleIds.forEach(id => {
      if (!selectedVisibleIds.includes(id)) recordCache.delete(id);
    });
    (selectedRows ?? []).forEach(row => {
      const id = this.asId(row['id']);
      if (id) recordCache.set(id, row);
    });

    if (kind === 'environments') this.selectedEnvironmentIds = nextIds;
    else this.selectedAssetIds = nextIds;
    this.refreshView();
  }

  async nextStep(): Promise<void> {
    this.submitError = '';
    if (this.currentStep === 1) {
      this.form.markAllAsTouched();
      if (this.form.invalid) return;
      this.currentStep = 2;
      if (!this.environmentsState.loaded) await this.fetchEnvironments(false);
      return;
    }
    if (this.currentStep === 2) {
      this.currentStep = 3;
      if (!this.assetsState.loaded) await this.fetchAssets(false);
      return;
    }
    if (this.currentStep === 3) this.currentStep = 4;
  }

  previousStep(): void {
    this.submitError = '';
    if (this.currentStep > 1) this.currentStep = (this.currentStep - 1) as WizardStep;
  }

  goToStep(step: WizardStep): void {
    if (step < this.currentStep) {
      this.currentStep = step;
      this.submitError = '';
    }
  }

  async finish(): Promise<void> {
    this.submitError = '';
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.currentStep = 1;
      this.submitError = 'Complete the required zone details before saving.';
      return;
    }
    if (!this.isCreate && !this.zoneId) {
      this.submitError = 'The zone ID is missing. The zone cannot be updated.';
      return;
    }

    this.isSubmitting = true;
    try {
const values = this.form.getRawValue();

const args: [
  string,
  string,
  string,
  string,
  string,
  string[],
  string[]
] = [
  String(values.zoneName ?? '').trim(),
  String(values.description ?? '').trim(),
  String(values.securityZoneTypeId ?? '').trim(),
  String(values.platformName ?? '').trim(),
  String(values.risk ?? '').trim(),
  [...this.selectedEnvironmentIds],
  [...this.selectedAssetIds]
];

if (this.isCreate) {
  await this.zoneService.createZone(...args).toPromise();
} else {
  await this.zoneService.updateZone(this.zoneId, ...args).toPromise();
}

      this.submitSuccess = true;
      const dialogRef = this.dialog.open(SuccessDialogComponent, {
        width: '280px',
        data: { isCreate: this.isCreate, isCreateUpdate: true, title: 'Security Zone' }
      });
      dialogRef.afterClosed().subscribe(() => {
        void this.workflowNavigation.navigateToWorkflow(
          'securityZoneManagement',
          ['zone', 'search']
        );
      });
    } catch (error) {
      console.error(this.isCreate ? 'Unable to create zone:' : 'Unable to update zone:', error);
      this.submitError = this.isCreate
        ? 'The security zone could not be created. Please try again.'
        : 'The security zone could not be updated. Please try again.';
      this.dialog.open(ErrorDialogComponent, {
        width: '280px',
        data: { errorMessage: this.submitError }
      });
    } finally {
      this.isSubmitting = false;
      this.refreshView();
    }
  }

  cancel(): void {
     void this.workflowNavigation.navigateToWorkflow(
    'securityZoneManagement',
    ['zone', 'search']
  );
  }

  navigateToZone(): void {
  void this.workflowNavigation.navigateToWorkflow(
    'securityZoneManagement',
    ['zone', 'search']
  );
  }

 navigateToPlatformCreate(): void {
  void this.workflowNavigation.navigateToWorkflow(
    'securityZoneManagement',
    ['platform', 'create']
  );
}

navigateToEnvironmentCreate(): void {
  void this.workflowNavigation.navigateToWorkflow(
    'securityZoneManagement',
    ['environment', 'create']
  );
}

  private normalizeEnvironment(item: ResourceRecord): ResourceRecord {
    const id = this.asId(item['id'] ?? item['environmentId'] ?? item['environment_id']);
    return {
      ...item,
      id,
      environmentId: id,
      environmentName: item['environmentName'] ?? item['name'] ?? item['environment_name'] ?? '—',
      description: item['description'] ?? '—'
    };
  }

  private normalizeAsset(item: ResourceRecord): ResourceRecord {
    const id = this.asId(item['assetId'] ?? item['systemAssetId'] ?? item['id'] ?? item['asset_id']);
    return {
      ...item,
      id,
      assetId: id,
      assetName: item['assetName'] ?? item['systemAssetName'] ?? item['name'] ?? '—',
      description: item['description'] ?? item['assetDescription'] ?? '—',
      businessValue: item['businessValue'] ?? '—',
      dataClassification: item['dataClassification'] ?? '—'
    };
  }

  private updateSelectedRecords(
    rows: ResourceRecord[],
    selectedIds: string[],
    cache: Map<string, ResourceRecord>
  ): void {
    rows.forEach(row => {
      const id = this.asId(row['id']);
      if (id && selectedIds.includes(id)) cache.set(id, row);
    });
  }

  private seedAssociationRecords(
    value: unknown,
    cache: Map<string, ResourceRecord>,
    selectedIds: string[],
    nameField: string
  ): void {
    const items = this.collectionFrom(value);
    items.forEach(item => {
      const id = this.asId(item['id'] ?? item['environmentId'] ?? item['environment_id'] ?? item['assetId'] ?? item['systemAssetId'] ?? item['value']);
      if (!id || !selectedIds.includes(id)) return;
      const name = item['environmentName'] ?? item['assetName'] ?? item['systemAssetName'] ?? item['name'];
      cache.set(id, { ...item, id, [nameField]: name ?? (nameField === 'assetName' ? 'Selected system asset' : 'Selected environment') });
    });
  }

  private extractAssociationIds(
    source: ResourceRecord,
    collectionKeys: string[],
    idKeys: string[]
  ): string[] {
    for (const key of collectionKeys) {
      const value = source[key];
      if (value === undefined || value === null) continue;
      if (typeof value === 'string' || typeof value === 'number') {
        if (key.toLowerCase().endsWith('ids')) return [String(value)];
        continue;
      }
      const items = this.collectionFrom(value);
      if (items.length) {
        const ids = items.map(item => {
          if (typeof item === 'string' || typeof item === 'number') return String(item);
          for (const idKey of idKeys) {
            const id = this.asId(item[idKey]);
            if (id) return id;
          }
          return this.asId(item['value']);
        }).filter(Boolean);
        if (ids.length) return ids;
      }
      if (Array.isArray(value) && value.length === 0) return [];
    }
    return [];
  }

  private collectionFrom(value: unknown): ResourceRecord[] {
    if (Array.isArray(value)) {
      return value.filter(item => item !== null && item !== undefined).map(item =>
        typeof item === 'object' ? item as ResourceRecord : ({ value: item } as ResourceRecord));
    }
    if (!value || typeof value !== 'object') return [];
    const record = value as ResourceRecord;
    const collectionKeys = [
      'Resource retrieved successfully', 'environments', 'assets', 'systemAssets', 'platforms',
      'securityZoneTypes', 'zoneTypes', 'data', 'content', 'items', 'results', 'records'
    ];
    for (const key of collectionKeys) {
      if (Array.isArray(record[key])) return this.collectionFrom(record[key]);
    }
    // A zone association may be returned as one resource object instead of an array.
    if (['id', 'environmentId', 'assetId', 'systemAssetId', 'name', 'environmentName', 'assetName'].some(key => key in record)) {
      return [record];
    }
    return [];
  }

  private recordFrom(value: unknown, wrapperKeys: string[]): ResourceRecord | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const record = value as ResourceRecord;
    for (const key of wrapperKeys) {
      const wrapped = record[key];
      if (wrapped && typeof wrapped === 'object' && !Array.isArray(wrapped)) return wrapped as ResourceRecord;
    }
    return record;
  }

  private asRecord(value: unknown): ResourceRecord | null {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as ResourceRecord : null;
  }

  private asId(value: unknown): string {
    return value === undefined || value === null ? '' : String(value).trim();
  }

  private optionalString(value: unknown): string | undefined {
    return value === undefined || value === null ? undefined : String(value);
  }

  private uniqueIds(values: string[]): string[] {
    return [...new Set(values.map(value => this.asId(value)).filter(Boolean))];
  }

  private readNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  private refreshView(): void {
    this.cdr.markForCheck();
  }
}
